/**
 * Error handling utilities for parsing and formatting API errors.
 * Provides consistent error extraction from Axios errors and user-friendly message formatting.
 *
 * STD-020 PR B (post-strip): backend emits only the RFC 7807 envelope; the
 * legacy ``message`` / ``error`` / ``timestamp`` / ``field_errors`` fields
 * are gone. Read priority: ``data.detail`` (RFC 7807) → HTTP-status
 * fallback. Validation errors use ``data.validation_errors`` exclusively.
 */

import {
  isAxiosErrorWithResponse,
  isValidationErrorResponse,
  type ValidationError,
} from '../types/errors';

/**
 * Extract user-friendly error message from any error type.
 * Attempts to extract detailed message from backend error response.
 *
 * @param error - The error to extract message from (can be any type)
 * @param fallbackMessage - Optional fallback if no message can be extracted
 * @returns User-friendly error message
 */
export function extractErrorMessage(error: unknown, fallbackMessage?: string): string {
  // Log full error for debugging
  console.error('Error occurred:', error);

  // A timeout also has no `response`, so it must be recognised before the
  // no-response branch below or it would be reported as an unreachable server.
  if (isTimeoutError(error)) {
    return 'Request timed out. Please try again.';
  }

  // An axios error with NO `response` never got an answer from the server: the
  // request was blocked before it left the browser (CORS preflight refused, a
  // blocking extension), or the connection dropped mid-flight — which a long
  // upload can genuinely do. Without this branch it fell through to
  // `error instanceof Error` and surfaced axios's bare "Network Error", a
  // string that names nothing and reads exactly like Wi-Fi dropping; that cost
  // a debugging session on the release-publish CORS failure (docs/GOTCHAS.md
  // G-7). Kept deliberately generic — it must fit the dropped-connection case
  // too, and only the console can say which one it was.
  if (isNetworkError(error)) {
    return 'The request never reached the server. Check your connection, then the browser console for the underlying network error.';
  }

  // Handle Axios errors with response
  if (isAxiosErrorWithResponse(error)) {
    const data = error.response?.data;

    // Extract error_code for logging (RFC 7807 §3.2 extension member kept
    // for triage; not yet replaced by per-class ``type`` URIs).
    if (data?.error_code) {
      console.error('Error code:', data.error_code);
    }

    // Extract details for logging
    if (data?.details) {
      console.error('Error details:', data.details);
    }

    // Validation errors — RFC 7807 ``validation_errors`` array shape
    // ``[{loc, msg, type}]``. The custom-ValidationError handler translates
    // its legacy ``{field: msg}`` dict into this shape server-side (PR B).
    if (
      data &&
      isValidationErrorResponse(data) &&
      data.validation_errors &&
      data.validation_errors.length > 0
    ) {
      const formatted = formatValidationErrors(data.validation_errors);
      if (formatted) {
        return formatted;
      }
    }

    // RFC 7807 ``detail`` is the canonical user-visible explanation.
    if (typeof data === 'object' && data !== null && 'detail' in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail) {
        return detail;
      }
      // Defense-in-depth: a raw FastAPI Pydantic-error array could only
      // reach here if a route bypasses ``setup_exception_handlers``
      // (misconfigured proxy, sub-app registered before the global wire-up).
      // Read ``msg`` only — never JSON-stringify the entry, which can carry
      // ``input``/``ctx`` (the submitted value; CWE-209).
      if (Array.isArray(detail) && detail.length > 0) {
        return detail
          .map((err: unknown) =>
            typeof err === 'object' && err !== null && 'msg' in err
              ? (err as { msg?: string }).msg || '[validation error]'
              : '[validation error]'
          )
          .join(', ');
      }
    }

    // Use HTTP status message as fallback
    if (error.response?.status) {
      const statusMessage = getHttpStatusMessage(error.response.status);
      if (statusMessage) {
        return statusMessage;
      }
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Final fallback
  return fallbackMessage || 'An unexpected error occurred. Please try again.';
}

/**
 * Extract error code from error response if available.
 *
 * Reads the ``error_code`` RFC 7807 §3.2 extension member. Will move to
 * ``type``-URI parsing if the backend adopts per-class type URIs.
 *
 * @param error - The error to extract code from
 * @returns Error code string or undefined
 */
export function extractErrorCode(error: unknown): string | undefined {
  if (isAxiosErrorWithResponse(error)) {
    return error.response?.data?.error_code || undefined;
  }
  return undefined;
}

/**
 * Extract error details object from error response if available.
 *
 * @param error - The error to extract details from
 * @returns Error details object or undefined
 */
export function extractErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (isAxiosErrorWithResponse(error)) {
    return error.response?.data?.details || undefined;
  }
  return undefined;
}

/**
 * Format RFC 7807 ``validation_errors`` array into a user-friendly message.
 *
 * Each entry has shape ``{loc: (string | number)[], msg: string, type: string}``
 * (FastAPI/pydantic shape, post server-side sanitization). The ``loc`` array
 * is dot-joined and Title-Cased for display.
 *
 * @param errors - The RFC 7807 validation_errors array
 * @returns Formatted error message or empty string
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) {
    return '';
  }

  const fieldLabel = (e: ValidationError): string => {
    // Empty loc (theoretical — FastAPI always populates) would otherwise
    // render ``: some msg`` with a leading colon; skip the prefix in that case.
    const name = formatFieldName(locToFieldName(e.loc));
    return name ? `${name}: ${e.msg}` : e.msg;
  };

  if (errors.length === 1) {
    return fieldLabel(errors[0]);
  }

  const formatted = errors.map((e) => `• ${fieldLabel(e)}`).join('\n');
  return `Validation errors:\n${formatted}`;
}

function locToFieldName(loc: Array<string | number>): string {
  // Strip the leading body/query/path scope marker if present — it adds no
  // user-relevant info (e.g. ["body", "serial"] → "serial").
  const tail =
    loc.length > 1 && (loc[0] === 'body' || loc[0] === 'query' || loc[0] === 'path')
      ? loc.slice(1)
      : loc;
  return tail.map((part) => String(part)).join('.');
}

/**
 * Format field name from snake_case to Title Case.
 *
 * @param fieldName - Field name in snake_case
 * @returns Formatted field name
 */
function formatFieldName(fieldName: string): string {
  return fieldName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get user-friendly message for HTTP status codes.
 *
 * @param status - HTTP status code
 * @returns User-friendly message or empty string
 */
export function getHttpStatusMessage(status: number): string {
  const statusMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input.',
    401: 'Authentication required. Please log in.',
    403: 'Permission denied. You do not have access to this resource.',
    404: 'Resource not found.',
    409: 'Conflict. The resource already exists or cannot be modified.',
    422: 'Validation failed. Please check your input.',
    429: 'Too many requests. Please try again later.',
    500: 'Server error. Please try again later.',
    502: 'Bad gateway. The server is temporarily unavailable.',
    503: 'Service temporarily unavailable. Please try again later.',
    504: 'Request timeout. The server took too long to respond.',
  };

  return statusMessages[status] || '';
}

/**
 * Check if error is a network error (no response from server).
 *
 * @param error - The error to check
 * @returns True if network error
 */
export function isNetworkError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'isAxiosError' in error) {
    const axiosError = error as { isAxiosError?: boolean; response?: unknown };
    return axiosError.isAxiosError === true && !axiosError.response;
  }
  return false;
}

/**
 * Check if error is a timeout error.
 *
 * @param error - The error to check
 * @returns True if timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return (error as { code?: string }).code === 'ECONNABORTED';
  }
  return false;
}

/**
 * Get comprehensive error message including network and timeout errors.
 *
 * Kept as the axios interceptor's entry point (and the name components import
 * for query errors), but it no longer carries its own timeout/no-response
 * strings: `extractErrorMessage` handles both, and having the two functions
 * word the same failure differently meant a CORS-blocked request read as
 * "Connection failed" in one surface and "Network Error" in another.
 *
 * @param error - The error to format
 * @param fallbackMessage - Optional fallback message
 * @returns Formatted error message
 */
export function getErrorMessage(error: unknown, fallbackMessage?: string): string {
  return extractErrorMessage(error, fallbackMessage);
}
